/**
 * Fill a session plan's enemy with its SRD attacks before it spawns.
 *
 * Session plans list AC, HP and ability scores for each enemy, but no
 * attacks, so spawned monsters only had the fallback unarmed strike. This
 * looks the enemy up on Open5e and returns the attacks and defenses to pass
 * to spawnMob. Anything the plan writes by hand wins over the lookup.
 */
import type { EncounterEnemy, EncounterEnemyAction, Open5eMonster, Open5eMonsterAction } from '@machina/core';

const DAMAGE_TYPES = [
  'slashing', 'piercing', 'bludgeoning', 'fire', 'cold', 'lightning',
  'thunder', 'acid', 'poison', 'necrotic', 'radiant', 'force', 'psychic',
];

/** Open5e action → monster attack. Null for actions with no roll (Multiattack). */
export function parseOpen5eAction(action: Open5eMonsterAction): EncounterEnemyAction | null {
  if (action.attack_bonus === undefined && !action.damage_dice) return null;

  const desc = action.desc.toLowerCase();
  const range: 'melee' | 'ranged' =
    desc.includes('ranged') || desc.includes('range') ? 'ranged' : 'melee';
  const damageType = DAMAGE_TYPES.find((t) => desc.includes(t)) ?? 'untyped';

  let damageDice = action.damage_dice || '1d4';
  if (action.damage_bonus && action.damage_bonus !== 0) {
    const sign = action.damage_bonus > 0 ? '+' : '';
    damageDice = `${damageDice}${sign}${action.damage_bonus}`;
  }

  return {
    name: action.name,
    attackBonus: action.attack_bonus || 0,
    damageDice,
    damageType,
    range,
    description: action.desc,
  };
}

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/** Slugs to try, in order: the plan's slug, the name in parentheses, the whole name. */
export function candidateSlugs(enemy: Pick<EncounterEnemy, 'name' | 'open5eSlug'>): string[] {
  const out: string[] = [];
  if (enemy.open5eSlug) out.push(enemy.open5eSlug);
  const paren = enemy.name.match(/\(([^)]+)\)\s*$/);
  if (paren) out.push(slugify(paren[1]));
  out.push(slugify(enemy.name.replace(/\([^)]*\)/g, '')));
  return [...new Set(out.filter(Boolean))];
}

/** Damage defenses stay one entry: their commas belong to phrases like "bludgeoning, piercing, and slashing from nonmagical attacks". */
function wholeList(s: string | undefined): string[] {
  return s && s.trim() ? [s.trim()] : [];
}

function commaList(s: string | undefined): string[] {
  return (s ?? '').split(',').map((x) => x.trim()).filter(Boolean);
}

export interface EnemySrdFill {
  actions: EncounterEnemyAction[];
  resistances: string[];
  immunities: string[];
  conditionImmunities: string[];
  open5eSlug?: string;
  speed?: number;
  cr?: number;
  type?: string;
}

export type FetchMonster = (slug: string) => Promise<Open5eMonster>;

/**
 * Attacks and defenses for one enemy. Never throws: when no lookup works
 * the enemy keeps whatever the plan wrote (possibly nothing), as before.
 */
export async function fillEnemyFromSrd(enemy: EncounterEnemy, fetchMonster: FetchMonster): Promise<EnemySrdFill> {
  let monster: Open5eMonster | null = null;
  // Only look up when the plan left something for the SRD to fill.
  const needsLookup = !enemy.actions?.length || !enemy.resistances || !enemy.immunities || !enemy.conditionImmunities;
  if (needsLookup) {
    for (const slug of candidateSlugs(enemy)) {
      try {
        monster = await fetchMonster(slug);
        break;
      } catch {
        // Not an SRD slug; try the next one.
      }
    }
  }

  const srdActions = (monster?.actions ?? [])
    .map(parseOpen5eAction)
    .filter((a): a is EncounterEnemyAction => a !== null);

  return {
    actions: enemy.actions?.length ? enemy.actions : srdActions,
    resistances: enemy.resistances ?? wholeList(monster?.damage_resistances),
    immunities: enemy.immunities ?? wholeList(monster?.damage_immunities),
    conditionImmunities: enemy.conditionImmunities ?? commaList(monster?.condition_immunities),
    open5eSlug: monster?.slug ?? enemy.open5eSlug,
    speed: monster?.speed?.walk,
    cr: monster?.cr,
    type: monster?.type,
  };
}
